// BuildingApprovalDetailPage.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import styles from "../../gmanewstyle.module.css"; // ← UPDATED TO UNIVERSAL STYLE
import logoimage from "../assets/Group -2.png";
import PortalSidebar from "../PortalSidebar/PortalSidebar";


const BuildingApprovalDetailPage = () => {
  const { approvalId } = useParams();
  const navigate = useNavigate();

  const [approvalInfo, setApprovalInfo] = useState(null);
  const [individualVotes, setIndividualVotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const [userAccess, setUserAccess] = useState([]);
  const [loadingAccess, setLoadingAccess] = useState(true);

  const linkRoutes = [
    { idField: "_cj_linkedaod_value", route: "aod", withBuilding: true },
    {
      idField: "_cj_linkedaodrecord_value",
      route: "legal",
      withBuilding: true,
    },
    {
      idField: "_cj_linkedpayment_value",
      route: "payment",
      withBuilding: true,
    },
    {
      idField: "_cj_linkedmaintenancejob_value",
      route: "maintenance",
      withBuilding: true,
    },
  ];

  const [displayName, setDisplayName] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Default sidebar state based on device width
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

useEffect(() => {
  const handleResize = () => {
    const desktop = window.innerWidth >= 1024;
    setIsDesktop(desktop);

    // Only force closed when moving to smaller screens.
    // On desktop, keep whatever state the user last chose.
    if (!desktop) {
      setSidebarOpen(false);
    }
  };

  handleResize();
  window.addEventListener("resize", handleResize);

  return () => window.removeEventListener("resize", handleResize);
}, []);
  useEffect(() => {
  function handleClick(e) {
    // On desktop/laptop, only the burger should control the sidebar
    if (isDesktop) return;

    const sidebar = document.getElementsByClassName(styles.sidebar)[0];
    const hamburger = document.getElementsByClassName(styles.menuBtn)[0];

    if (!sidebar || !hamburger) return;

    const clickedInsideSidebar = sidebar.contains(e.target);
    const clickedHamburger = hamburger.contains(e.target);

    if (sidebarOpen && !clickedInsideSidebar && !clickedHamburger) {
      setSidebarOpen(false);
    }
  }

  document.addEventListener("click", handleClick);
  return () => document.removeEventListener("click", handleClick);
}, [sidebarOpen, isDesktop]);

  // pull user access for top-right dropdown
  useEffect(() => {
    const fetchUserAccess = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      try {
        const decoded = JSON.parse(atob(token.split(".")[1]));
        const { email, contact_id, firstname, lastname, name } = decoded || {};

        // Set display name safely
        const fullName =
          firstname && lastname
            ? `${firstname} ${lastname}`
            : name
            ? name
            : email || "User";

        setDisplayName(fullName);

        if (!email) {
          setLoadingAccess(false);
          return;
        }

        const resp = await fetch(
          "https://portal.gmacc.co.za/api/contact/determine-access/",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ email, contact_id }),
          }
        );

        const data = await resp.json();
        if (resp.ok) {
          setUserAccess(data.access || []);
        }
      } catch (err) {
        console.error("Failed to fetch access:", err);
      } finally {
        setLoadingAccess(false);
      }
    };

    fetchUserAccess();
  }, [navigate]);

  const handleOwnerPortalClick = () => navigate("/owner-portal");
  const handleTrusteePortalClick = () => navigate("/trustee-portal");
  const handleContractorPortalClick = () => navigate("/contractor-portal");

  /* -----------------------------------------
         FETCH APPROVAL + VOTES DATA
  ------------------------------------------*/
  useEffect(() => {
    document.title = "Building Approval Detail";

    const token = localStorage.getItem("token");
    if (!token) return navigate("/");

    const loadData = async () => {
      try {
        const [res1, res2] = await Promise.all([
          fetch(
            `https://portal.gmacc.co.za/api/building-approval/${approvalId}/`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                Accept: "application/json",
                "Content-Type": "application/json",
              },
            }
          ),
          fetch(
            `https://portal.gmacc.co.za/api/building-approval/${approvalId}/votes/`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                Accept: "application/json",
                "Content-Type": "application/json",
              },
            }
          ),
        ]);

        const approvalData = await res1.json();
        const votesData = await res2.json();

        if (!res1.ok || !res2.ok) throw new Error("Failed");

        setApprovalInfo(approvalData);
        setIndividualVotes(votesData);
      } catch (e) {
        setError("Failed to load building approval information.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [approvalId, navigate]);

  /* -----------------------------------------
              FORMAT HELPERS
  ------------------------------------------*/
  const formatAmount = (amount) => {
    const num = parseFloat(String(amount).replace(/[R,]/g, ""));
    if (isNaN(num)) return amount;
    return "R " + num.toFixed(2);
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "-";
    return format(d, "dd MMM yyyy HH:mm");
  };

  /* -----------------------------------------
          TOP TABLE FIELDS MAPPING
  ------------------------------------------*/
  let topTableFields = [];
  let amountValue = "";

  if (approvalInfo) {
    topTableFields = [
      { label: "Subject", field: "subject" },
      { label: "Building", field: "building" },
      { label: "Linked Reference", field: "linkedReference" },
      { label: "Approval Type", field: "approvalType" },
      { label: "Start Date", field: "startDate" },
      { label: "End Date", field: "endDate" },
    ];

    const match = approvalInfo.description?.match(/R[\d,]+(?:\.\d+)?/);
    amountValue = match ? formatAmount(match[0]) : "";

    if (amountValue) {
      topTableFields.push({ label: "Amount", field: "amount" });
    }
  }

  /* -----------------------------------------
                SORT VOTES
  ------------------------------------------*/
  const sortedVotes = individualVotes.slice().sort((a, b) => {
    const order = [
      "Approved",
      "Declined",
      "Abstained",
      "Abstained due to inactivity",
    ];
    return order.indexOf(a.response) - order.indexOf(b.response);
  });

  const computedApprovalCount = individualVotes.filter(
    (v) => v.response === "Approved"
  ).length;
  const computedDeclineCount = individualVotes.filter(
    (v) => v.response === "Declined"
  ).length;
  const computedAbstainCount = individualVotes.filter(
    (v) =>
      v.response === "Abstained" || v.response === "Abstained due to inactivity"
  ).length;
  /* ============================================================
                      PAGE RENDER
  ============================================================ */
  return (
    <div className={styles.page}>
      <header className={styles.topbar}>
        <div className={styles.topbarLeft}>
          <img src={logoimage} alt="GMA Logo" className={styles.topLogo} />

          {/* HAMBURGER: opens/closes sidebar */}
          <button
            type="button"
            className={styles.menuBtn}
            onClick={() => setSidebarOpen((v) => !v)}
          >
            ☰
          </button>

          <h1 className={styles.topTitle}>Building Approval Detail</h1>
        </div>

        {/* USER CHIP: opens dropdown */}
        <div
          className={styles.userChip}
          onClick={() => {
            navigate("/user-profile");
          }}
          style={{ cursor: "pointer" }}
        >
          <span className={styles.statusDot}></span>
          <span className={styles.userName}>{displayName} ▾</span>
        </div>
      </header>

      <div className={styles.shell}>
<PortalSidebar
  activePortal="trustee"
  activeTab="approvals"
  userAccess={userAccess}
  loadingAccess={loadingAccess}
  sidebarOpen={sidebarOpen}
  ticketRole="trustee"
/>
        {/* =====================================================
                             LOADING / ERROR
        ====================================================== */}
        {loading ? (
          <div className={styles.page}>
            <div className={styles.shell}>
              <main className={styles.contentArea}>
                <div className={styles.panel}>
                  <div className={styles.loadingContainer}>
                    <div className={styles.loadingImage}></div>
                    <p className={styles.loadingText}>Loading...</p>
                  </div>
                </div>
              </main>
            </div>
          </div>
        ) : error ? (
          <p className={styles.errorText}>{error}</p>
        ) : (
          <>
            <main className={styles.contentArea}>
              <div className={styles.panel}>
                <div className={styles.paymentHeaderCard}>
                  <button
                    className={styles.paymentBackBtn}
                    onClick={() => navigate(-1)}
                  >
                    Back
                  </button>

                  {/* TITLE */}
                  <h2 className={styles.paymentHeaderTitle}>
                    BUILDING APPROVAL INFORMATION
                  </h2>

                  {/* DIVIDER */}
                  <div className={styles.paymentHeaderDivider} />

                  {/* HEADER LABELS — AUTO GRID */}
                  <div
                    className={styles.paymentHeaderGridAuto}
                    style={{ "--col-count": topTableFields.length }}
                  >
                    {topTableFields.map((item) => (
                      <div
                        key={item.label}
                        className={styles.paymentHeaderLabelItem}
                      >
                        {item.label}:
                      </div>
                    ))}
                  </div>

                  {/* HEADER VALUES — WHITE PILL */}
                  <div className={styles.paymentHeaderValuePill}>
                    <div
                      className={styles.paymentHeaderGridAuto}
                      style={{ "--col-count": topTableFields.length }}
                    >
                      {topTableFields.map((item, index) => {
                        const value =
                          item.field === "amount" ? (
                            amountValue
                          ) : item.field === "startDate" ||
                            item.field === "endDate" ? (
                            formatDateTime(approvalInfo[item.field])
                          ) : item.field === "linkedReference" ? (
                            <span
                              className={styles.relatedLinkDark}
                              onClick={() => {
                                try {
                                  const match = linkRoutes.find(
                                    (r) =>
                                      r.idField ===
                                      approvalInfo.linkedReferenceField
                                  );
                                  if (!match) return;

                                  const id = approvalInfo.linkedReferenceId;
                                  const buildingId = approvalInfo.buildingGuid;

                                  const path = match.withBuilding
                                    ? `/${match.route}/${id}/${buildingId}`
                                    : `/${match.route}/${id}`;

                                  navigate(path);
                                } catch (e) {
                                  console.error(
                                    "Linked reference navigation error",
                                    e
                                  );
                                }
                              }}
                            >
                              {approvalInfo[item.field]}
                            </span>
                          ) : (
                            approvalInfo[item.field]
                          );

                        return (
                          <div
                            key={index}
                            className={styles.paymentHeaderValueItem}
                          >
                            {value}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* =====================================================
                     LOWER CARDS WRAPPER (3 COLUMNS)
            ====================================================== */}
                <div className={styles.paymentCardGrid}>
                  {/* =====================================================
                        APPROVAL STATUS INFORMATION CARD
              ====================================================== */}
                  <div className={styles.paymentCardDark}>
                    <h2 className={styles.cardSectionTitle}>
                      APPROVAL STATUS INFORMATION
                    </h2>

                    {[
                      { label: "Approval Status", field: "approvedStatus" },
                      { label: "Approval Count", field: "approvalCount" },
                      { label: "Decline Count", field: "declineCount" },
                      { label: "Abstain Count", field: "abstainCount" },
                      {
                        label: "Required Approvals",
                        field: "requiredApprovals",
                      },
                    ].map((item, index) => (
                      <div key={index} className={styles.inputGroup}>
                        <h3 className={styles.inputLabel}>{item.label}:</h3>

                        <input
                          readOnly
                          className={styles.inputField}
                          value={
                            item.field === "approvalCount"
                              ? computedApprovalCount
                              : item.field === "declineCount"
                              ? computedDeclineCount
                              : item.field === "abstainCount"
                              ? computedAbstainCount
                              : approvalInfo[item.field]
                          }
                        />
                      </div>
                    ))}
                  </div>

                  {/* =====================================================
             VOTES INFORMATION CARD (Style 5)
====================================================== */}
                  <div className={styles.paymentCard}>
                    <h2 className={styles.cardSectionTitleLight}>
                      VOTES INFORMATION
                    </h2>

                    {sortedVotes.length === 0 ? (
                      <p className={styles.gmaInfoText}>No votes yet.</p>
                    ) : (
                      sortedVotes.map((vote, idx) => (
                        <div
                          key={idx}
                          className={styles.inputGroupLight}
                          data-response={vote.response}
                        >
                          <div className={styles.inputGroupLight}>
                            <h3 className={styles.inputLabelLight}>Trustee:</h3>
                            <input
                              className={styles.inputFieldLight}
                              readOnly
                              value={vote.trustee}
                            />
                          </div>

                          <div className={styles.inputGroupLight}>
                            <h3 className={styles.inputLabelLight}>
                              Response:
                            </h3>
                            <input
                              className={styles.inputFieldLight}
                              readOnly
                              value={vote.response}
                            />
                          </div>

                          <div className={styles.inputGroupLight}>
                            <h3 className={styles.inputLabelLight}>
                              Response Date:
                            </h3>
                            <input
                              className={styles.inputFieldLight}
                              readOnly
                              value={formatDateTime(vote.date)}
                            />
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* =====================================================
             OTHER INFORMATION CARD (Style 5)
====================================================== */}
                  <div className={styles.paymentCard}>
                    <h2 className={styles.cardSectionTitleLight}>OTHER</h2>

                    {/* DOCUMENT DOWNLOAD */}
                    <div className={styles.inputGroupLight}>
                      <h3 className={styles.inputLabelLight}>Document:</h3>
                      <a
                        target="_blank"
                        rel="noreferrer"
                        href={approvalInfo.fileLink}
                        className={`${styles.inputFieldLight} ${styles.relatedLink}`}
                      >
                        Download File
                      </a>
                    </div>

                    {/* APPROVAL NOTES (Option A — inside a pill box) */}
                    <div className={styles.inputGroupLight}>
                      <h3 className={styles.inputLabelLight}>
                        Approval Notes:
                      </h3>

                      <div
                        className={styles.inputFieldLightLong}
                        style={{ whiteSpace: "pre-wrap" }}
                      >
                        {approvalInfo.description}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </>
        )}
      </div>
    </div>
  );
};

export default BuildingApprovalDetailPage;
